import { PrinterOutlined, ReloadOutlined } from '@ant-design/icons';
import type { PurchaseItemRow, PurchaseRow } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatAmount, formatDisplayTime, formatQty } from '@hq/utils';
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Alert, Button, Card, Descriptions, Drawer, Segmented, Space, Spin, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import { hq } from '../api/client';
import { MoneyText } from '../components/Money';
import { ellipsisCell, sumWidth } from '@hq/ui-kit';
import { printDocument } from '../print';

const PAGE_SIZE = 20;

const STATUS_META: Record<string, { text: string; color: string }> = {
  DRAFT: { text: '草稿', color: 'default' },
  SUBMITTED: { text: '已提交', color: 'processing' },
  APPROVED: { text: '已批准', color: 'blue' },
  SENT: { text: '已下达', color: 'blue' },
  ACKED: { text: '供应商已接单', color: 'cyan' },
  PART_RECEIVED: { text: '部分到货', color: 'orange' },
  RECEIVED: { text: '已到货', color: 'green' },
  CLOSED: { text: '已关闭', color: 'default' },
  CANCELED: { text: '已取消', color: 'default' }
};

/**
 * 采购订单只读跟踪（docs/01 §6.2-6）：
 * 医院端只关心「我的请购转成了哪张采购单、到货多少」，写操作留在运营端 / 供应商门户。
 */
export default function PurchaseList() {
  const [status, setStatus] = useState<string>('ALL');
  const [currentId, setCurrentId] = useState<string | null>(null);

  const query = useInfiniteQuery({
    queryKey: ['purchase', 'list', status],
    placeholderData: keepPreviousData,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      hq.procurement.purchases.list({
        lastId: pageParam,
        size: PAGE_SIZE,
        orderStatus: status === 'ALL' ? undefined : status
      }),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextLastId : undefined)
  });

  const detail = useQuery({
    queryKey: ['purchase', 'detail', currentId],
    placeholderData: keepPreviousData,
    queryFn: () => hq.procurement.purchases.detail(currentId ?? ''),
    enabled: Boolean(currentId)
  });

  const rows = query.data?.pages.flatMap((page) => page.items) ?? [];

  const columns: ColumnsType<PurchaseRow> = [
    {
      title: '采购单号',
      dataIndex: 'bizNo',
      width: 210,
      render: (value: string, row) => <Typography.Link onClick={() => setCurrentId(row.id)}>{value}</Typography.Link>
    },
    { title: '供应商', dataIndex: 'supplierName', width: 180, render: (value: string | null) => value || '-' },
    {
      title: '状态',
      dataIndex: 'orderStatus',
      width: 120,
      render: (value: string) => {
        const meta = STATUS_META[value] ?? { text: value, color: 'default' };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    { title: '数量', dataIndex: 'totalQty', width: 100, align: 'right', render: (value: number) => formatQty(value) },
    { title: '金额', dataIndex: 'totalAmount', width: 130, align: 'right', render: (value: number) => <MoneyText value={value} /> },
    { title: '已到货', dataIndex: 'receivedQty', width: 100, align: 'right', render: (value: number) => formatQty(value) },
    { title: '预计到货', dataIndex: 'expectArriveAt', width: 150, render: (value: string | null) => formatDisplayTime(value) },
    { title: '下单时间', dataIndex: 'bizDate', width: 150, render: (value: string) => formatDisplayTime(value) }
  ];

  const itemColumns: ColumnsType<PurchaseItemRow> = [
    { title: '行', dataIndex: 'lineNo', width: 56 },
    { title: '物资编码', dataIndex: 'materialCode', width: 150, render: (value: string | null) => value || '-' },
    { title: '物资名称', dataIndex: 'materialName', ellipsis: { showTitle: false }, render: ellipsisCell('materialName') },
    { title: '规格', dataIndex: 'spec', width: 140, render: (value: string | null) => value || '-' },
    { title: '单位', dataIndex: 'unitName', width: 80, render: (value: string | null) => value || '-' },
    { title: '订购量', dataIndex: 'qty', width: 100, align: 'right', render: (value: number) => formatQty(value) },
    { title: '到货量', dataIndex: 'receivedQty', width: 100, align: 'right', render: (value: number) => formatQty(value) },
    { title: '单价', dataIndex: 'unitPrice', width: 110, align: 'right', render: (value: number) => formatAmount(value, 4) },
    {
      title: '税率',
      dataIndex: 'taxRate',
      width: 80,
      align: 'right',
      render: (value: number) => `${formatQty(value * 100)}%`
    },
    { title: '金额', dataIndex: 'amount', width: 120, align: 'right', render: (value: number) => formatAmount(value) }
  ];

  const print = () => {
    const doc = detail.data;
    if (!doc) return;
    printDocument({
      title: '采购订单',
      subtitle: doc.order.bizNo,
      meta: [
        ['供应商', doc.order.supplierName || '-'],
        ['状态', STATUS_META[doc.order.orderStatus]?.text ?? doc.order.orderStatus],
        ['下单时间', formatDisplayTime(doc.order.bizDate)],
        ['预计到货', formatDisplayTime(doc.order.expectArriveAt)],
        ['数量合计', formatQty(doc.order.totalQty)],
        ['金额合计', formatAmount(doc.order.totalAmount)]
      ],
      columns: [
        { title: '行' },
        { title: '物资编码' },
        { title: '物资名称' },
        { title: '规格' },
        { title: '单位' },
        { title: '订购量', align: 'right' },
        { title: '到货量', align: 'right' },
        { title: '单价', align: 'right' },
        { title: '金额', align: 'right' }
      ],
      rows: doc.items.map((item) => [
        item.lineNo,
        item.materialCode ?? '',
        item.materialName ?? '',
        item.spec ?? '',
        item.unitName ?? '',
        formatQty(item.qty),
        formatQty(item.receivedQty),
        formatAmount(item.unitPrice, 4),
        formatAmount(item.amount)
      ]),
      footer: `备注：${doc.order.remark || '-'}`
    });
  };

  return (
    <Card
      className="page-card"
      title="采购订单"
      extra={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => query.refetch()} loading={query.isFetching}>
            刷新
          </Button>
          <Button icon={<PrinterOutlined />} disabled={!detail.data} onClick={print}>
            打印
          </Button>
        </Space>
      }
    >
      <Segmented
        value={status}
        onChange={(value) => setStatus(String(value))}
        options={[
          { label: '全部', value: 'ALL' },
          { label: '已下达', value: 'SENT' },
          { label: '已接单', value: 'ACKED' },
          { label: '已到货', value: 'RECEIVED' }
        ]}
        style={{ marginBottom: 12 }}
      />

      {query.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="采购订单加载失败"
          description={query.error instanceof ApiError ? query.error.friendlyMessage : String(query.error)}
        />
      ) : null}

      <Table<PurchaseRow>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={query.isPending}
        pagination={false}
        scroll={{ x: sumWidth(columns) }}
        locale={{ emptyText: query.isError ? '加载失败，请重试' : '暂无采购订单' }}
        onRow={(row) => ({ onDoubleClick: () => setCurrentId(row.id) })}
      />

      <Space style={{ marginTop: 16 }}>
        <Typography.Text type="secondary">已加载 {rows.length} 条</Typography.Text>
        {query.hasNextPage ? (
          <Button onClick={() => query.fetchNextPage()} loading={query.isFetchingNextPage}>
            加载更多
          </Button>
        ) : null}
      </Space>

      <Drawer
        title={detail.data?.order.bizNo ?? '采购订单详情'}
        open={currentId !== null}
        onClose={() => setCurrentId(null)}
        width={920}
        extra={
          <Button icon={<PrinterOutlined />} onClick={print} disabled={!detail.data}>
            打印
          </Button>
        }
      >
        {detail.isLoading ? (
          <Spin />
        ) : detail.isError ? (
          <Alert
            type="error"
            showIcon
            message="详情加载失败"
            description={detail.error instanceof ApiError ? detail.error.friendlyMessage : String(detail.error)}
          />
        ) : detail.data ? (
          <>
            <Descriptions size="small" column={2} bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="状态">
                {STATUS_META[detail.data.order.orderStatus]?.text ?? detail.data.order.orderStatus}
              </Descriptions.Item>
              <Descriptions.Item label="供应商">{detail.data.order.supplierName || '-'}</Descriptions.Item>
              <Descriptions.Item label="订单类型">{detail.data.order.orderType || '-'}</Descriptions.Item>
              <Descriptions.Item label="付款状态">{detail.data.order.payStatus || '-'}</Descriptions.Item>
              <Descriptions.Item label="预计到货">{formatDisplayTime(detail.data.order.expectArriveAt)}</Descriptions.Item>
              <Descriptions.Item label="供应商接单">{formatDisplayTime(detail.data.order.supplierAckAt)}</Descriptions.Item>
              <Descriptions.Item label="金额合计">{formatAmount(detail.data.order.totalAmount)}</Descriptions.Item>
              <Descriptions.Item label="已到货金额">{formatAmount(detail.data.order.receivedAmount)}</Descriptions.Item>
            </Descriptions>
            <Table<PurchaseItemRow>
              rowKey="id"
              size="small"
              columns={itemColumns}
              dataSource={detail.data.items}
              pagination={false}
              scroll={{ x: sumWidth(itemColumns) }}
            />
          </>
        ) : null}
      </Drawer>
    </Card>
  );
}
